import { useEffect, useState } from "react";
import { Menu, X, Instagram, Youtube, Mail, ShoppingBag } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { SECTIONS, SOCIALS } from "../config";
import { useCart } from "../lib/cart";
import { useSiteConfig } from "../lib/siteConfig";

const CONTACT_MAILTO = "mailto:info@phillyfreeway.shop";
const CONTACT_LABEL = "Email Philadelphia Freeway";

// Compact, symmetrical navbar. Three-column layout so the two-line brand block
// sits optically centered on the viewport regardless of logo/hamburger width.
// Height ~64px mobile / ~70px desktop. Hamburger tap target 44x44, borderless.
// Sits flush beneath the announcement bar via top = var(--banner-h).
export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const cart = useCart();
  const count = cart?.count || 0;
  const { products_available } = useSiteConfig();
  // TEMPORARY availability mode: hide the cart entry points from the nav
  // while purchases are disabled. All cart code stays wired for when the
  // flag flips back to true.
  const showCart = !!products_available;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Route-aware nav handler. SECTIONS entries with a `path` property navigate
  // to that route via React Router; the rest are in-page smooth-scroll anchors.
  // If we're currently NOT on the homepage but the user clicks a scroll anchor,
  // we route home first and rely on the id existing on that page.
  const handleNav = (item) => {
    setOpen(false);
    if (item.path) {
      navigate(item.path);
      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }
    if (window.location.pathname !== "/") {
      navigate(`/#${item.id}`);
      setTimeout(() => {
        const el = document.getElementById(item.id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      return;
    }
    const el = document.getElementById(item.id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <header
        data-testid="site-navbar"
        style={{ top: 0 }}
        className={`fixed left-0 right-0 z-40 transition-colors duration-500 ${
          scrolled ? "bg-black/85 backdrop-blur-xl border-b border-white/10" : "bg-black/40 backdrop-blur-md"
        }`}
      >
        <div
          className="grid items-center px-3 md:px-8"
          style={{
            minHeight: 64,
            gridTemplateColumns: "1fr auto",
          }}
        >
          {/* LEFT: logo only (no brand title text) */}
          <Link
            to="/"
            data-testid="nav-brand"
            aria-label="Philadelphia Freeway home"
            className="flex items-center justify-start"
            onClick={() => window.scrollTo({ top: 0, behavior: "auto" })}
          >
            <img
              src="/assets/logos/logo_gold.png"
              alt="Philadelphia Freeway"
              className="object-contain"
              style={{ width: 44, height: 44 }}
            />
          </Link>

          {/* RIGHT: desktop nav OR mobile hamburger + cart */}
          <div className="flex items-center justify-end">
            <nav className="hidden lg:flex items-center gap-5 xl:gap-7 mr-2">
              {SECTIONS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-link-${l.id}`}
                  onClick={() => handleNav(l)}
                  className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/70 hover:text-[#D4AF37] transition-colors whitespace-nowrap"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={SOCIALS.instagram.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={SOCIALS.instagram.label}
                data-testid="nav-ig"
                className="text-white/70 hover:text-[#D4AF37] transition-colors"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href={SOCIALS.youtube.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={SOCIALS.youtube.label}
                data-testid="nav-yt"
                className="text-white/70 hover:text-[#D4AF37] transition-colors"
              >
                <Youtube className="w-4 h-4" />
              </a>
              <a
                href={CONTACT_MAILTO}
                aria-label={CONTACT_LABEL}
                data-testid="nav-email"
                className="text-white/70 hover:text-[#D4AF37] transition-colors"
              >
                <Mail className="w-4 h-4" />
              </a>
              <Link to="/cart" aria-label="Cart" data-testid="nav-cart"
                className={`relative text-white/85 hover:text-[#F4C10A] transition-colors ml-1 ${showCart ? "" : "hidden"}`}>
                <ShoppingBag className="w-5 h-5" />
                {count > 0 && <span data-testid="nav-cart-count" className="absolute -top-2 -right-2 bg-[#F4C10A] text-black text-[9px] font-mono w-4 h-4 rounded-full flex items-center justify-center">{count}</span>}
              </Link>
            </nav>

            <Link to="/cart" aria-label="Cart" data-testid="nav-cart-mobile"
              className={`lg:hidden relative text-white hover:text-[#F4C10A] mr-2 ${showCart ? "" : "hidden"}`}>
              <ShoppingBag className="w-5 h-5" />
              {count > 0 && <span className="absolute -top-2 -right-2 bg-[#F4C10A] text-black text-[9px] font-mono w-4 h-4 rounded-full flex items-center justify-center">{count}</span>}
            </Link>

            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden inline-flex items-center justify-center text-white hover:text-[#D4AF37] transition-colors"
              style={{ width: 44, height: 44 }}
              aria-label="Toggle menu"
              aria-expanded={open}
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden fixed inset-0 z-30 bg-black/95 backdrop-blur-xl pt-28 px-6"
        >
          <ul className="space-y-5 w-full">
            {SECTIONS.map((l) => (
              <li key={l.id} className="w-full">
                <button
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => handleNav(l)}
                  className="font-wide text-white text-2xl uppercase tracking-tight hover:text-[#D4AF37] block text-left w-full leading-[1.05]"
                >
                  {l.label}
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex gap-3 items-center">
            <a
              href={SOCIALS.instagram.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={SOCIALS.instagram.label}
              data-testid="mobile-menu-ig"
              className="text-white/70 hover:text-[#D4AF37] border border-white/20 p-3"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href={SOCIALS.youtube.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={SOCIALS.youtube.label}
              data-testid="mobile-menu-yt"
              className="text-white/70 hover:text-[#D4AF37] border border-white/20 p-3"
            >
              <Youtube className="w-5 h-5" />
            </a>
            <a
              href={CONTACT_MAILTO}
              aria-label={CONTACT_LABEL}
              data-testid="mobile-menu-email"
              className="text-white/70 hover:text-[#D4AF37] border border-white/20 p-3"
            >
              <Mail className="w-5 h-5" />
            </a>
          </div>
        </div>
      )}
    </>
  );
}
